import { emitFlagMessage } from '@cae/cobalt-react/components/flag-message';
import { useAuth } from '@contexts/auth-provider/hooks/use-auth/useAuth';
import { useOrganizations } from '@contexts/organizations-provider';
import { getInvoiceFile } from '@features/invoices/api/get-invoice-file/getInvoiceFile';
import { FLAG_MESSAGE_DURATION } from '@shared/constants/flagMessage.constants';
import * as React from 'react';

import type {
  UseDownloadFileParams,
  UseDownloadFileReturn,
} from './useDownloadFile.types';

export function useDownloadFile({
  id,
  filename,
  errorMessage,
}: UseDownloadFileParams): UseDownloadFileReturn {
  const { isAuthenticated } = useAuth();
  const { selectedOrganization } = useOrganizations();

  const [isDownloading, setIsDownloading] = React.useState(false);

  const handleDownload = React.useCallback(async (): Promise<void> => {
    if (!isAuthenticated || !selectedOrganization) return;

    setIsDownloading(true);
    try {
      const blob = await getInvoiceFile({
        id,
        organizationId: selectedOrganization.id,
      });

      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      emitFlagMessage('danger', errorMessage, FLAG_MESSAGE_DURATION);
    } finally {
      setIsDownloading(false);
    }
  }, [id, filename, errorMessage, isAuthenticated, selectedOrganization]);

  return { handleDownload, isDownloading };
}
